var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import * as React from "react";
import { useMutationObserver } from "../../../internal-hooks/useMutationObserver";
import { classnames, generateTestId } from "../../../utils";
import { Text } from "../../Text";
import { RequiredMessage } from "../../TextField/TextField/styled";
import { DropDown, SelectFieldStyle } from "./styled";
import Downshift from "downshift";
import { CloseIcon } from "../../Icons/CloseIcon";
import { SearchIcon } from "../../Icons/SearchIcon";
import { ChevronDownIcon } from "../../Icons/ChevronDownIcon";
function defaultRenderDropdownItem(item) {
    return React.createElement("li", null, itemToString(item));
}
var SelectFieldAsync = function (_a) {
    var id = _a.id, style = _a.style, label = _a.label, _b = _a.isRequired, isRequired = _b === void 0 ? false : _b, _c = _a.hasError, hasError = _c === void 0 ? false : _c, _d = _a.placeholder, placeholder = _d === void 0 ? "" : _d, footerMessage = _a.footerMessage, itemsToDisplay = _a.itemsToDisplay, searchable = _a.searchable, value = _a.value, _e = _a.dropdownWidth, dropdownWidth = _e === void 0 ? "auto" : _e, _f = _a.renderDropdownItem, renderDropdownItem = _f === void 0 ? defaultRenderDropdownItem : _f, onRequestItems = _a.onRequestItems, onChange = _a.onChange, onSearchValueChange = _a.onSearchValueChange, externalSearchInputValue = _a.searchValue, onClearSearch = _a.onClearSearch, onOpen = _a.onOpen, onScroll = _a.onScroll;
    var widgetRef = React.useRef(null);
    var menuRef = React.useRef(null);
    var _g = __read(React.useState(0), 2), selfWidth = _g[0], setSelfWidth = _g[1];
    React.useLayoutEffect(function () {
        var _a, _b, _c;
        setSelfWidth((_c = (_b = (_a = widgetRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect()) === null || _b === void 0 ? void 0 : _b.width) !== null && _c !== void 0 ? _c : 0);
    }, [widgetRef.current]);
    useMutationObserver(widgetRef.current, function () {
        var _a, _b, _c;
        setSelfWidth((_c = (_b = (_a = widgetRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect()) === null || _b === void 0 ? void 0 : _b.width) !== null && _c !== void 0 ? _c : 0);
    });
    var _h = __read(React.useState(false), 2), isOpen = _h[0], setIsOpen = _h[1];
    var _j = __read(React.useState(""), 2), defaultSearchInputValue = _j[0], setDefaultSearchInputValue = _j[1];
    React.useEffect(function () {
        if (!searchable) {
            setDefaultSearchInputValue("");
        }
    }, [searchable]);
    var searchInputValue = searchable
        ? externalSearchInputValue !== null && externalSearchInputValue !== void 0 ? externalSearchInputValue : defaultSearchInputValue
        : "";
    var _k = __read(React.useState([]), 2), items = _k[0], setItems = _k[1];
    var onSearchInputValueChanged = React.useCallback(function (value) {
        if (!searchable) {
            return;
        }
        if (onSearchValueChange) {
            onSearchValueChange(value);
        }
        else {
            setDefaultSearchInputValue(value);
        }
    }, [searchable, onSearchValueChange]);
    React.useEffect(function () {
        onRequestItems(searchInputValue)
            .then(function (items) {
            setItems(items);
        })
            .catch(function (e) {
            console.error(e);
        });
    }, [searchInputValue, onRequestItems]);
    var handleOpen = React.useCallback(function () {
        if (isOpen) {
            return;
        }
        if (onOpen) {
            onOpen();
        }
        setIsOpen(true);
    }, [isOpen, onOpen]);
    var handleInputClick = React.useCallback(function () {
        if (!isOpen) {
            setDefaultSearchInputValue("");
            handleOpen();
        }
    }, [isOpen, handleOpen]);
    var onDismiss = React.useCallback(function () {
        setIsOpen(false);
    }, []);
    var onValueChanged = React.useCallback(function (value) {
        onChange(value);
        setIsOpen(false);
    }, [onChange]);
    var inputValue = searchInputValue;
    if (!isOpen) {
        if (value) {
            inputValue = itemToString(value);
        }
        else {
            inputValue = "";
        }
    }
    var hasContent = inputValue && inputValue.length > 0;
    var width = undefined;
    var minWidth = undefined;
    if (dropdownWidth === "field") {
        width = selfWidth;
    }
    else if (dropdownWidth === "auto" ||
        isNaN(Number(dropdownWidth)) ||
        Number(dropdownWidth) <= 0) {
        minWidth = Math.max(selfWidth, 100);
    }
    else {
        width = dropdownWidth;
    }
    return (React.createElement(SelectFieldStyle, null,
        label && (React.createElement(React.Fragment, null,
            React.createElement(Text, { as: "label", variant: "label" }, label),
            isRequired && (React.createElement(RequiredMessage, __assign({ "$hasError": hasError }, generateTestId("label-required"), generateTestId(hasError ? "label-required-error" : "label-required-no-error")), "(required)")))),
        React.createElement(Downshift, { onChange: onValueChanged, isOpen: isOpen, onOuterClick: onDismiss, selectedItem: value, inputValue: inputValue, onInputValueChange: searchable ? onSearchInputValueChanged : undefined, itemToString: itemToString }, function (_a) {
            var getInputProps = _a.getInputProps, getItemProps = _a.getItemProps, getMenuProps = _a.getMenuProps, getToggleButtonProps = _a.getToggleButtonProps, isOpen = _a.isOpen, clearSelection = _a.clearSelection, getRootProps = _a.getRootProps;
            var displayItems = items;
            if (itemsToDisplay && itemsToDisplay > 0) {
                displayItems = items.slice(0, itemsToDisplay);
            }
            return (React.createElement("div", { ref: widgetRef },
                React.createElement("div", __assign({ id: id, style: style, className: classnames("bn-select-widget", isOpen && "bn-select-widget--focused", hasError && "bn-select-widget--error") }, getRootProps({}, { suppressRefError: true })),
                    React.createElement("input", __assign({}, getInputProps(), { onClick: handleInputClick, className: classnames("bn-select-input", hasError && "bn-select-input--error"), readOnly: !searchable, placeholder: placeholder, value: isOpen ? getInputProps().value : inputValue !== null && inputValue !== void 0 ? inputValue : "" })),
                    hasContent && (React.createElement(CloseIcon, { size: "small", onClick: function () {
                            clearSelection();
                            if (searchable) {
                                if (onClearSearch) {
                                    onClearSearch();
                                }
                                else if (onSearchValueChange) {
                                    onSearchValueChange("");
                                }
                            }
                        }, "aria-label": "clear selection", className: "bn-select-clear-icon" })),
                    !hasContent && searchable && (React.createElement(SearchIcon, __assign({}, getToggleButtonProps(), { onClick: handleOpen, size: "small", "aria-label": "toggle menu", className: "bn-select-search-icon" }))),
                    !hasContent && !searchable && (React.createElement(ChevronDownIcon, __assign({}, getToggleButtonProps(), { onClick: handleOpen, size: "small", "aria-label": "toggle menu", className: "bn-select-open-icon" })))),
                React.createElement(DropDown, { isOpen: isOpen, anchorEl: widgetRef.current, placement: "bottom-start", width: width, minWidth: minWidth },
                    React.createElement("ul", __assign({}, getMenuProps({ ref: menuRef }, {
                        suppressRefError: true
                    }), { className: "bn-dropdown", onScroll: onScroll }),
                        isOpen
                            ? displayItems.map(function (item, index) {
                                var renderedItem = renderDropdownItem(item, {
                                    index: index
                                });
                                var listItem = React.cloneElement(renderedItem, __assign({}, getItemProps({
                                    className: classnames("bn-select-item", renderedItem.props.className),
                                    key: item.value,
                                    index: index,
                                    item: item
                                })));
                                return listItem;
                            })
                            : null,
                        footerMessage && footerMessage.length > 0 && (React.createElement("li", { className: "bn-select-footer-message-item" },
                            React.createElement(Text, { as: "div", variant: "body" }, footerMessage))),
                        itemsToDisplay && items.length > itemsToDisplay && (React.createElement("li", { className: "bn-select-footer-message-item" },
                            React.createElement(Text, { as: "div", variant: "body" },
                                "Showing results",
                                " ",
                                Math.min(itemsToDisplay, displayItems.length),
                                " of total",
                                " ",
                                items.length,
                                ". Please refine your search.")))))));
        })));
};
export default SelectFieldAsync;
function itemToString(item) {
    var _a;
    if (!item) {
        return "";
    }
    return (_a = item.label) !== null && _a !== void 0 ? _a : String(item.value);
}
//# sourceMappingURL=SelectFieldAsync.js.map